import {
  filterFinanceAvailableRowsRemovingSystem,
  logFinanceSystemRowRenderFilter,
} from "./financeSystemBalanceRow";

/**
 * Wallet summary for agent finance / dashboard (single SSOT rules).
 *
 * Production bug: `possibleProfit` (getFinanceData) can duplicate the sum of
 * `/availableOrdersCommission` lines (1098 + 1098 → 2196 in UI if combined).
 * We never add possibleProfit + availableLinesSum.
 *
 * v4 rule:
 * - After flatten + system-balance row removal, **countable** lines =
 *   rows where `is_withdrawable !== false`, plus rows that explicitly
 *   `affects_balance` (a subscription wallet debit is not withdrawable,
 *   but it still reduces the headline).
 * - If any countable line exists: headline **availableTotal** / **withdrawableTotal**
 *   come from those lines only (withdrawable also respects `affects_withdrawable_balance`).
 * - If none: **fallback** to `Number(possibleProfit) || 0` for both.
 *
 * `availableLinesSum` / `availableRowsCount` = all non-system lines (audit vs API list).
 *
 * futureTotal  = SUM(future[].commission)
 * historyTotal = SUM(history[].commission)
 */

export const WALLET_SUMMARY_CALCULATION_VERSION = "calculated_wallet_v4_available_lines_first";

/** Rows that count toward agent headline totals.
 * A line that is not itself withdrawable still counts when `affects_balance` is true,
 * so a subscription debit reduces יתרה זמינה and יתרה למשיכה.
 */
export function isCountableAgentAvailableApiRow(row) {
  if (row == null || typeof row !== "object") {
    return false;
  }
  if (row.affects_balance === true) {
    return true;
  }
  if (typeof row.is_withdrawable === "boolean" && row.is_withdrawable === false) {
    return false;
  }
  return true;
}

export function logFinanceAvailableTotalSourceTrace(payload) {
  const enabled =
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    String(import.meta.env?.VITE_FINANCE_AVAILABLE_TOTAL_TRACE || "").trim() === "1";
  if (!enabled) {
    return;
  }
  try {
    console.info("[FINANCE_AVAILABLE_TOTAL_SOURCE_TRACE]", JSON.stringify(payload));
  } catch {
    console.info("[FINANCE_AVAILABLE_TOTAL_SOURCE_TRACE]", payload);
  }
}

/** DEV: guard trace when balancing UI totals against row lists. */
export function logFinanceDoubleCountGuard(payload) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) {
    return;
  }
  try {
    console.info("[FINANCE_DOUBLE_COUNT_GUARD]", JSON.stringify(payload));
  } catch {
    console.info("[FINANCE_DOUBLE_COUNT_GUARD]", payload);
  }
}
/** ערך עמלה משורת API (יישור ל-FutureCommissionDetails / גיוון שדות משרתים). */
export function commissionNumericFromLine(row) {
  if (row == null || typeof row !== "object") {
    return NaN;
  }
  const raw =
    row.commission ??
    row.Commission ??
    row.amount ??
    row.Amount ??
    row.possibleProfit ??
    row.PossibleProfit;
  const v = Number(raw);
  return Number.isFinite(v) ? v : NaN;
}

export function sumCommissionsFromLines(lines) {
  if (!Array.isArray(lines)) {
    return { total: 0, rows_count: 0 };
  }
  let total = 0;
  for (const row of lines) {
    const v = commissionNumericFromLine(row);
    if (Number.isFinite(v)) {
      total += v;
    }
  }
  return { total, rows_count: lines.length };
}

/** Normalizes /availableOrdersCommission body: { gimmonix, lds, dynamic } */
export function flattenAvailableBuckets(data) {
  if (data == null) {
    return [];
  }
  let payload = data;
  let depth = 0;
  while (
    depth < 8 &&
    typeof payload === "object" &&
    payload !== null &&
    !Array.isArray(payload) &&
    payload.data != null &&
    typeof payload.data === "object"
  ) {
    const inner = payload.data;
    if (Array.isArray(inner)) {
      return inner;
    }
    if (
      Array.isArray(inner.gimmonix) ||
      Array.isArray(inner.lds) ||
      Array.isArray(inner.dynamic) ||
      Array.isArray(inner.manual)
    ) {
      payload = inner;
      break;
    }
    payload = inner;
    depth++;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (typeof payload !== "object" || payload === null) {
    return [];
  }
  return [
    ...(Array.isArray(payload.gimmonix) ? payload.gimmonix : []),
    ...(Array.isArray(payload.lds) ? payload.lds : []),
    ...(Array.isArray(payload.dynamic) ? payload.dynamic : []),
    ...(Array.isArray(payload.manual) ? payload.manual : []),
  ];
}

/** Flatten + drop system-balance ledger rows (not shown / not selectable). */
export function flattenAvailableBucketsExcludingSystemBalance(data) {
  const raw = flattenAvailableBuckets(data);
  return filterFinanceAvailableRowsRemovingSystem(raw).kept;
}

/**
 * @param {unknown[]} [opts.futureLines] — תוצאת /futureOrdersCommission
 * @param {object|null} [opts.availableBuckets] — גוף data מ-/availableOrdersCommission
 * @param {unknown[]} [opts.historyLines] — מערך שטוח מ-/ordersCommissionHistory
 * @param {unknown} [opts.possibleProfit] — fallback when no countable available lines
 */
export function buildWalletSummary({
  futureLines = [],
  availableBuckets = null,
  historyLines = [],
  possibleProfit = null,
} = {}) {
  const avLinesRaw = flattenAvailableBuckets(availableBuckets);
  const { kept: avLines, removed: avRemovedSystem } =
    filterFinanceAvailableRowsRemovingSystem(avLinesRaw);
  if (avRemovedSystem.length) {
    logFinanceSystemRowRenderFilter(avRemovedSystem);
  }
  const avSum = sumCommissionsFromLines(avLines);
  const countableAvailableLines = avLines.filter(isCountableAgentAvailableApiRow);
  const countableSum = sumCommissionsFromLines(countableAvailableLines);
  const withdrawableLines = countableAvailableLines.filter((row) => {
    const explicitAffects = row?.affects_withdrawable_balance;
    if (typeof explicitAffects === "boolean") {
      return explicitAffects;
    }
    return true;
  });
  const withdrawableLinesSum = sumCommissionsFromLines(withdrawableLines);
  const ppNum = Number(possibleProfit);
  const pp = Number.isFinite(ppNum) ? ppNum : 0;

  const fut = sumCommissionsFromLines(Array.isArray(futureLines) ? futureLines : []);
  const hist = sumCommissionsFromLines(Array.isArray(historyLines) ? historyLines : []);

  const useLineTotals = countableAvailableLines.length > 0;
  const finalAvailableTotal = useLineTotals
    ? Math.round(countableSum.total * 100) / 100
    : Math.round(pp * 100) / 100;
  const finalWithdrawableTotal = useLineTotals
    ? Math.round(withdrawableLinesSum.total * 100) / 100
    : Math.round(pp * 100) / 100;
  const source = useLineTotals ? "available_lines" : "possible_profit_fallback";

  logFinanceAvailableTotalSourceTrace({
    possibleProfit: pp,
    availableLinesSum: avSum.total,
    availableRowsCount: avSum.rows_count,
    countableRowsCount: countableAvailableLines.length,
    finalAvailableTotal,
    finalWithdrawableTotal,
    source,
  });
  logFinanceDoubleCountGuard({
    possibleProfit: pp,
    rowsSum: avSum.total,
    finalAvailableTotal,
  });

  return {
    availableLinesSum: avSum.total,
    availableTotal: finalAvailableTotal,
    withdrawableLinesSum: withdrawableLinesSum.total,
    withdrawableTotal: finalWithdrawableTotal,
    systemBalance: finalAvailableTotal,
    futureTotal: fut.total,
    historyTotal: hist.total,
    futureRowsCount: fut.rows_count,
    availableRowsCount: avSum.rows_count,
    historyRowsCount: hist.rows_count,
    calculationVersion: WALLET_SUMMARY_CALCULATION_VERSION,
  };
}

/** DEV: לוג למקור נתונים בודד (סכום שורות commission בלבד). */
export function traceWalletSsotSource(source, lines) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) {
    return;
  }
  const { total, rows_count } = sumCommissionsFromLines(Array.isArray(lines) ? lines : []);
  console.log("[WALLET_SSOT_TRACE]", {
    source,
    total,
    rows_count,
    calculation_version: WALLET_SUMMARY_CALCULATION_VERSION,
  });
}
